import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import React, { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { IFilterOption } from '../../common/filter-bar/types';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { PaginationContext } from '../../../contexts/pagination-context';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import GenericTable from '../../common/generic-table/generic-table';
import { displayMoney } from '../../../utils/money';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

import RestorePageIcon from '@mui/icons-material/RestorePage';
import CreatePaymentTask from '../../common/create-payment-task/create-payment-task';
import InvoicePDFDialog from '../../common/invoice-pdf-dialog/invoice-pdf-dialog';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import FilterBar from '../../common/filter-bar/filter-bar';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import { IDriverInvoice } from '../../../interfaces/common/driver-invoices';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import AutocompleteWithThrottle from '../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { DeliveryState, PaymentState } from '../../../enums/orders';
import { showDriverInvoiceState } from '../../../utils/driver-invoices';
import { MerchantType } from '../../../interfaces/delivery-company/merchants';
import {
  IMerchantOrdersInvoice,
  MerchantOrdersInvoiceState,
} from '../../../interfaces/common/merchant-invoices';
import {
  canCancelMerchantInvoice,
  showMerchantInvoiceState,
} from '../../../utils/merchant-invoices';
import {
  defaultMerchantFooter,
  defaultMerchantReportFieldsConfig,
} from '../../common/report-builder/fields-config';
import { color } from 'framer-motion';
import { format } from 'date-fns';
import { ReportProps } from '../../common/report-builder/report';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { CANCELLED } from 'dns';
import { emptyTotalAmounts } from '../../../interfaces/common/invoice';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import ResponsiveMerchantInvoicesListing from '../common/merchant-invoices-listing';
import MerchantsBalance from '../../delivery-company-components/accouting/invoices/merchants_balance';

export function MerchantOrdersAccounting() {
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [invoicesList, setInvoicesList] = useState<IMerchantOrdersInvoice[]>(
    []
  );
  const [invoicesCount, setInvoicesCount] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });
  const { color } = useContext(OrgSettingsContext);
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [lockedInvoiceMeta, setLockedInvoiceMeta] =
    useState<IMerchantOrdersInvoice | null>(null);
  const [lockType, setLockType] = useState<'view' | 'delete' | null>(null);
  const [confirmUndoInvoice, setConfirmUndoInvoice] = useState(false);
  const [showingBalance, setShowingBalance] = useState(false);
  const [legacyPdfData, setLegacyPdfData] = useState<IMerchantOrdersInvoice | null>(null);

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const [paginationController, setPaginationController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPaginationController({
      ...paginationController,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setPaginationController({
      ...paginationController,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const resetPaginationController = () => {
    setPaginationController({
      ...paginationController,
      page: 0,
    });
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationController();
    setDateFilter(dateFilterValue);
  };

  const relevant_fields = [
    'id',
    'net_due_to_merchant',
    'merchant',
    'packages',
    'created_at',
    'status',
    'notes',
  ];

  useEffect(() => {
    let response: any;
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/merchant-invoices/?page=${paginationController.page + 1}&page_size=${paginationController.rowsPerPage}&fields=${encodeURIComponent(relevant_fields.join(','))}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      try {
        response = await axios.get<{
          count: number;
          results: IMerchantOrdersInvoice[];
        }>(endpoint, config);
        console.log(response.data);

        setResponse(response);
        setInvoicesList(response.data.results);
        setInvoicesCount(response.data.count);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          console.log('error: ', e);
          setResponse(e.response);
          setMessage('Failed to fetch invoices: ' + e.response?.statusText);
        } else {
          setMessage('Failed to fetch invoices');
        }
        console.log(e);
      }

      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
  }, [dateFilter, paginationController, filters]);

  const legacyFetchInvoiceContent = async (invoicemeta: IMerchantOrdersInvoice) => {
    setLockedInvoiceMeta(invoicemeta);
    setLockType('view');

    const endpoint = `${API_ENDPOINT}/api/v1/merchant-invoice/${invoicemeta.id}/`;

    try {
      const response = await axios.get(endpoint, config);
      setResponse(response);
      console.log('target = ', response);
      const data: IMerchantOrdersInvoice = response.data;
      setLegacyPdfData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to download invoice content: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to download invoice content');
      }
    }
    setLockedInvoiceMeta(null);
    setLockType(null);
  };

  const fetchInvoiceContent = async (invoicemeta: IMerchantOrdersInvoice) => {
    setLockedInvoiceMeta(invoicemeta);
    setLockType('view');

    const endpoint = `${API_ENDPOINT}/api/v1/merchant-invoice/${invoicemeta.id}/print/`;

    try {
      const response: AxiosResponse<ArrayBuffer> = await axios.get(endpoint, {responseType: 'arraybuffer', ...config});
      setResponse(response);
  
      const blob = new Blob([response.data], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      const formattedDate = format(
        invoicemeta.created_at,
        'dd-MM-yyyy'
      );

      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoicemeta.id}.${invoicemeta.merchant?.name ?? 'merchant'}.${formattedDate}.pdf`;
      link.click();

      URL.revokeObjectURL(url); 
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to download invoice content: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to download invoice content');
      }
    }
    setLockedInvoiceMeta(null);
    setLockType(null);
  }

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationController();
    setFilters(filters);
  };

  return (
    <div className="content-container">
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        My Invoices
      </Typography>
      <br />

      <Box>
        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}

        <br />
        <Button
          onClick={() => setShowingBalance(true)}
          variant="contained"
          color="warning"
        >
          My Balance
        </Button>
        <br />
        <Accordion elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography
              variant="h5"
              component="h5"
              sx={{ textAlign: 'center', mt: 1, mb: 1 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <ManageSearchIcon fontSize="large" color="primary" />
                <span>
                  <b>Search and Filter Controls</b>
                </span>
              </div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DoubleDatePicker dateFilterAction={dateFilterAction} />
            <br />
            <FilterBar
              filterQueryAction={filterQueryAction}
              currentlySelectedFilters={filters}
              filterKeys={['merchant invoice status', 'payment method']}
            />
          </AccordionDetails>
        </Accordion>

        <ResponsiveMerchantInvoicesListing
          invoicesList={invoicesList}
          isLoading={isLoading}
          setLockedInvoiceMeta={setLockedInvoiceMeta}
          setLockType={setLockType}
          fetchInvoiceContent={fetchInvoiceContent}
          legacyFetchInvoiceContent={legacyFetchInvoiceContent}
          lockedInvoiceMeta={lockedInvoiceMeta}
          lockType={lockType}
        />

        <TablePagination
          component="div"
          onPageChange={handlePageChange}
          page={paginationController.page}
          count={invoicesCount}
          rowsPerPage={paginationController.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
          labelRowsPerPage=""
          style={{ overflow: 'visible' }}
        />
      </Box>

      {showingBalance && (
        <MerchantsBalance
          title={'My Balance'}
          onClose={() => {
            setShowingBalance(false);
          }}
        />
      )}

      {legacyPdfData && legacyPdfData.merchant && (
              <InvoicePDFDialog
                pdfData={legacyPdfData}
                onClose={() => setLegacyPdfData(null)}
                merchant={legacyPdfData.merchant}
              />
            )}

      
    </div>
  );
}

export default MerchantOrdersAccounting;



  
